import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import tw, { styled } from 'twin.macro';

import ChevronIcon from '~/shared/assets/icons/chevron.svg';
import RefreshIcon from '~/shared/assets/icons/refresh.svg';
import CheckMarkIcon from '~/shared/assets/icons/checkmark.svg';
import Button from '~/shared/components/Buttons';
import Typography from '~/shared/components/Typography';

interface RefreshOption {
  title: string;
  time: number;
}

interface RefreshFilterDropdownProps {
  onClickRefresh: () => void;
  onSelect: (selection: RefreshOption) => void;
  items?: RefreshOption[];
  selectedOptionIndex?: number;
  isLoading?: boolean;
}

const ItemsContainer = tw(Menu.Items)`
   absolute w-[234px] right-1 top-[50px] origin-top-right font-light
   rounded-[8px] bg-white p-[12px]
   shadow-dropdown focus:outline-none z-50
 ` as typeof Menu.Items;

const MenuBtn = styled.button<{
  $active: boolean;
}>(({ $active }) => [
  tw`text-[16px] leading-6 mb-1 text-black hover:bg-primary-50
  w-full h-[48px] flex rounded-md px-2 py-[2px] items-center`,
  $active ? tw`bg-primary-100 text-primary-500 hover:bg-primary-100` : '',
]);

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

function RefreshFilterDropdown({
  onClickRefresh,
  onSelect,
  items,
  selectedOptionIndex = 0,
  isLoading,
}: RefreshFilterDropdownProps) {
  const [showCheckmark, setShowCheckmark] = useState(false);
  const selectedItem = items?.[selectedOptionIndex];

  const onClick = (item: RefreshOption) => {
    onSelect(item);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isLoading) {
      setShowCheckmark(true);
      timer = setTimeout(() => {
        setShowCheckmark(false);
      }, 3000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <Menu as="div" className="relative flex">
      <div className="flex">
        <Button
          variant="secondary"
          className="w-[52px] !border-r-0 !rounded-r-none group hover:bg-primary-50"
          onClick={onClickRefresh}
        >
          {showCheckmark ? (
            <CheckMarkIcon width={20} height={20} />
          ) : (
            <RefreshIcon
              width={20}
              height={20}
              className={clsx(
                isLoading ? 'animate-spin' : '',
                'fill-primary-900 group-hover:fill-primary-500'
              )}
            />
          )}
        </Button>
        <Menu.Button className="flex justify-center items-center py-3 px-2 min-w-[40px] border border-primary-500 rounded-r-lg gap-[4px] hover:bg-primary-50">
          {({ open }) => (
            <>
              <div className="font-normal text-[16px] leading-[20px] text-[#544A4A] h-full relative">
                {selectedItem?.title === 'Off' ? '' : selectedItem?.title}

                <ProgressBar
                  selectedItem={selectedItem}
                  onClickRefresh={onClickRefresh}
                  isLoading={isLoading}
                />
              </div>

              <ChevronIcon
                className={clsx('fill-[#1D1D1D]', open ? 'rotate-180' : '')}
                width={16}
                height={16}
              />
            </>
          )}
        </Menu.Button>
      </div>

      <Transition as={Fragment} {...transitionProps}>
        <ItemsContainer>
          <div className="py-[10px] px-[12px]">
            <Typography variant="p3" fontWeight={400} color="text-grey-2">
              Auto-refresh
            </Typography>
          </div>
          <div className="">
            {items?.map((item) => (
              <Menu.Item key={item.title}>
                <MenuBtn onClick={() => onClick(item)} $active={item.title === selectedItem?.title}>
                  {item.title}
                </MenuBtn>
              </Menu.Item>
            ))}
          </div>
        </ItemsContainer>
      </Transition>
    </Menu>
  );
}

export default RefreshFilterDropdown;

function ProgressBar({
  selectedItem,
  onClickRefresh,
  isLoading,
}: {
  selectedItem: RefreshOption | undefined;
  onClickRefresh: () => void;
  isLoading?: boolean;
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!selectedItem || selectedItem.time <= 0 || isLoading) {
      return;
    }

    const intervalTime = 100;
    const totalSteps = selectedItem.time / intervalTime;
    let currentStep = 0;

    const intervalId = setInterval(() => {
      currentStep += 1;
      const newProgress = Math.floor((currentStep / totalSteps) * 100);

      if (newProgress >= 100) {
        clearInterval(intervalId);
        setProgress(100);
        onClickRefresh();
      } else {
        setProgress(newProgress);
      }
    }, intervalTime);

    return () => clearInterval(intervalId);
  }, [selectedItem, onClickRefresh, isLoading]);

  return (
    <div className=" absolute bottom-[-6px] w-full h-[3px] bg-primary-50 rounded-lg overflow-hidden flex flex-col">
      <div
        className="h-full bg-primary-200 transition-all duration-50 ease-linear"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
}
